import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import Loader from 'src/designSystem/Loader'
import { useAdvancedOverviewState } from './api/AdvancedOverviewProvider'
import { useDashboardLayout } from './api/useDashboardLayout'
import { hasWindowContent } from './api/useLocalWindowLayout'
import { useWorkspaceTabs } from './api/useWorkspaceTabs'
import { advancedColors } from './theme'
import { PAGE_LIBRARY_CATEGORIES, PROGRESS_EXCLUDED_CATEGORIES } from './dashboard/tabs'
import { PanelOfferScope } from './dashboard/registry'
import AddComponentTile from './dashboard/AddComponentTile'
import DashboardCanvas from './dashboard/DashboardCanvas'
import DashboardCustomizeBar from './dashboard/DashboardCustomizeBar'
import DashboardCustomizeButton from './dashboard/DashboardCustomizeButton'
import DashboardGrid, { GridItem } from './dashboard/DashboardGrid'
import DashboardRenderer from './dashboard/DashboardRenderer'
import { useCustomizeBarOpen } from './dashboard/useCustomizeBarOpen'
import { useEditMode } from './dashboard/useEditMode'
import WindowEditor from './dashboard/WindowEditor'
import { useCurrentRole } from './currentRole'
import PatientRoleProvider from './PatientRoleProvider'
import PatientTabSwitcher from './components/PatientTabSwitcher'
import FeedChatPanel from './dashboard/chat/FeedChatPanel'
import FloatingCareTeamChat from './dashboard/chat/FloatingCareTeamChat'
import PatientFeedProvider from './dashboard/chat/PatientFeedProvider'
import { ForwardedFeedItem } from './dashboard/chat/types'
import { serviceUnreachableHint } from './serviceUnreachableHint'
import { tracksUnavailableHeadline } from './tracksUnavailableHeadline'
import { CallNotesRequest, CallNotesRequestProvider } from './dashboard/callNotesRequest'
import { canProvideCall } from './dashboard/callProvider'
import { CARE_PLAN_TAB_ID, MEDICAL_RECORD_TAB_ID, TabNavigationProvider } from './dashboard/tabNavigationContext'
import { useSharedCarePlan } from './api/useSharedCarePlan'
import { CarePlanState } from './panels/carePlanEditor/carePlanState'
import { TOUR_ANCHORS } from './tour/anchors'
import AdvancedTour from './tour/AdvancedTour'
import { CareTeamChatAccess, useLendCareTeamChatToProductUpdates } from './productUpdates/ProductUpdatesProvider'
import { useAdvancedSettings } from './api/AdvancedSettingsProvider'
import { canAccessCareTeamChat } from './dashboard/chat/careTeamChatAccess'
import { useMyCareTeamRoles } from './dashboard/chat/useMyCareTeamRoles'
import { ChatComposerLayoutProvider } from './dashboard/chat/ChatComposerLayout'
import { useHostSlots } from './hostSlots'

const Centered = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}
  >
    {children}
  </Box>
)

const NO_PLAN: Pick<CarePlanState, 'reviewPending'> = {}

const PROGRESS_SCOPE: PanelOfferScope = { excludeCategories: PROGRESS_EXCLUDED_CATEGORIES }

const careTeamChatAccess = (pending: boolean, canChat: boolean): CareTeamChatAccess => {
  if (pending) return 'pending'
  return canChat ? 'available' : 'unavailable'
}

type Props = { uid: string }

const PatientView = ({ uid }: Props) => {
  const state = useAdvancedOverviewState()
  const [role] = useCurrentRole()
  const { status, layout, isCustom, saveFailed, controls } = useDashboardLayout(role)
  const [customizeOpen, toggleCustomize] = useCustomizeBarOpen()
  const [editMode] = useEditMode()
  const { tabs, addTab, removeTab, setVisible, moveTo } = useWorkspaceTabs()
  const [careTeamChatOpen, setCareTeamChatOpen] = useState(false)
  const { settings } = useAdvancedSettings()
  const { renderCallHeader } = useHostSlots()
  const [callNotesRequest, setCallNotesRequest] = useState<CallNotesRequest | null>(null)
  const myRoles = useMyCareTeamRoles()
  const canChatWithCareTeam = !!settings && canAccessCareTeamChat(myRoles, settings.careTeamChatExcludedRoles)
  const [tourShowsChatPicker, setTourShowsChatPicker] = useState(false)
  const careTeamChatOpenNow = useRef(careTeamChatOpen)
  careTeamChatOpenNow.current = careTeamChatOpen
  // Put back on close, so a walkthrough doesn't shut a chat the clinician had open.
  const chatOpenBeforeTour = useRef<boolean | null>(null)
  const setCareTeamChatOpenFromTour = useCallback((open: boolean) => {
    if (open && chatOpenBeforeTour.current === null) {
      chatOpenBeforeTour.current = careTeamChatOpenNow.current
    }
    const next = open || (chatOpenBeforeTour.current ?? false)
    if (!open) {
      chatOpenBeforeTour.current = null
    }
    careTeamChatOpenNow.current = next
    setTourShowsChatPicker(open)
    setCareTeamChatOpen(next)
  }, [])
  useLendCareTeamChatToProductUpdates(
    careTeamChatAccess(!settings || state.status === 'loading', canChatWithCareTeam),
    setCareTeamChatOpenFromTour
  )
  const [activeTab, setActiveTab] = useState(() => tabs.find((tab) => tab.kind === 'feed')?.id ?? tabs[0]?.id)
  const [forward, setForward] = useState<ForwardedFeedItem | null>(null)
  // Read-only: badges the plan tab until the psychiatrist first saves a drafted plan.
  const masterPlan = useSharedCarePlan(uid, 'psychiatrist', NO_PLAN, {
    writable: false,
    enabled: role === 'psychiatrist',
  })

  useEffect(() => {
    const current = tabs.find((tab) => tab.id === activeTab)
    if (current?.visible) {
      return
    }
    const fallback = tabs.find((tab) => tab.visible) ?? tabs[0]
    if (fallback && fallback.id !== activeTab) {
      setActiveTab(fallback.id)
    }
  }, [tabs, activeTab])

  const progressLoading = state.status === 'loading' || status === 'loading'

  let progressContent: React.ReactNode

  if (progressLoading) {
    progressContent = (
      <Centered>
        <Loader size="large" />
      </Centered>
    )
  } else if (state.status !== 'ready') {
    progressContent = (
      <Centered>
        <Box sx={{ maxWidth: 380 }}>
          <Typography variant="h3">{tracksUnavailableHeadline(state.status === 'unavailable')}</Typography>
          <Typography variant="subtitle1" sx={{ mt: 1, color: advancedColors.inkMuted }}>
            {state.status === 'unavailable' ? 'You may not be on their Advanced care team.' : serviceUnreachableHint()}
          </Typography>
        </Box>
      </Centered>
    )
  } else {
    progressContent = (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DashboardCanvas>
            <DashboardRenderer
              uid={uid}
              layout={layout}
              controls={editMode ? controls : undefined}
              scope={PROGRESS_SCOPE}
            />

            {editMode && (
              <DashboardGrid>
                <GridItem span="full">
                  <AddComponentTile
                    uid={uid}
                    layout={layout}
                    onAdd={controls.add}
                    prominent={layout.panels.length === 0}
                    scope={PROGRESS_SCOPE}
                  />
                </GridItem>
              </DashboardGrid>
            )}
          </DashboardCanvas>
        </Box>

        {editMode &&
          (customizeOpen ? (
            <DashboardCustomizeBar
              layout={layout}
              isCustom={isCustom}
              saveFailed={saveFailed}
              controls={controls}
              onClose={toggleCustomize}
              scope={PROGRESS_SCOPE}
            />
          ) : (
            <DashboardCustomizeButton onOpen={toggleCustomize} />
          ))}
      </Box>
    )
  }

  const contentFor = (tab: (typeof tabs)[number]): boolean => {
    if (tab.kind === 'progress') {
      return layout.panels.length > 0
    }
    if (tab.kind === 'feed') {
      return true
    }
    return hasWindowContent(tab.id, role)
  }

  const tabsWithContent = tabs.map((tab) => ({ ...tab, hasContent: contentFor(tab) }))

  const tabNavigation = useMemo(
    () => ({
      openTab: setActiveTab,
      hasTab: (tabId: string) => tabs.some((tab) => tab.id === tabId && tab.visible),
    }),
    [tabs]
  )

  const feedActive = tabs.find((tab) => tab.id === activeTab)?.kind === 'feed'

  const openCallNotes = useCallback(
    (callID: string, entryID?: string | null) => {
      if (!entryID) {
        setCallNotesRequest({ uid, callID })
      }
      if (tabs.some((tab) => tab.id === MEDICAL_RECORD_TAB_ID && tab.visible)) {
        setActiveTab(MEDICAL_RECORD_TAB_ID)
      }
      if (entryID) {
        setTimeout(
          () => document.getElementById(`patient-history-${entryID}`)?.scrollIntoView?.({ block: 'start' }),
          100
        )
      }
    },
    [uid, tabs]
  )

  const showCallHeaderForProgram = useCallback(
    (programID: string | undefined) => canProvideCall(myRoles, programID),
    [myRoles]
  )

  return (
    <TabNavigationProvider value={tabNavigation}>
      <CallNotesRequestProvider value={callNotesRequest}>
        <AdvancedTour setCareTeamChatOpen={setCareTeamChatOpenFromTour} />
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
          {feedActive && renderCallHeader && (
            <Box sx={{ flexGrow: 0, zIndex: 1 }}>
              {renderCallHeader(uid, { onOpenCallNotes: openCallNotes, showForProgram: showCallHeaderForProgram })}
            </Box>
          )}
          <ChatComposerLayoutProvider synchronize={careTeamChatOpen && canChatWithCareTeam && state.status === 'ready'}>
            <PatientFeedProvider uid={uid} markSeenEnabled={feedActive}>
              <Box sx={{ display: 'flex', flex: 1, minHeight: 0, minWidth: 0, position: 'relative' }}>
                <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
                  {tabs.map((tab) => (
                    <Box
                      // Window tabs remount on role change; layout re-read is usePersistedState's job.
                      key={tab.kind === 'window' ? `${tab.id}-${role}` : tab.id}
                      data-tour={TOUR_ANCHORS.tabContent(tab.id)}
                      sx={{ display: activeTab === tab.id ? 'block' : 'none', height: '100%' }}
                    >
                      {tab.kind === 'progress' && progressContent}
                      {tab.kind === 'feed' && (
                        <FeedChatPanel
                          uid={uid}
                          careTeamChatOpen={careTeamChatOpen && canChatWithCareTeam && state.status === 'ready'}
                          onForwardToCareTeam={
                            canChatWithCareTeam
                              ? (item) => {
                                  setForward(item)
                                  setCareTeamChatOpen(true)
                                }
                              : undefined
                          }
                        />
                      )}
                      {tab.kind === 'window' && (
                        <WindowEditor
                          uid={uid}
                          windowId={tab.id}
                          role={role}
                          editMode={editMode}
                          onlyCategories={PAGE_LIBRARY_CATEGORIES[tab.id]}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
                {state.status === 'ready' && canChatWithCareTeam && (
                  <FloatingCareTeamChat
                    uid={uid}
                    open={careTeamChatOpen}
                    onOpenChange={setCareTeamChatOpen}
                    pendingForward={forward}
                    onClearPendingForward={() => setForward(null)}
                    showPicker={tourShowsChatPicker}
                  />
                )}
              </Box>
            </PatientFeedProvider>
          </ChatComposerLayoutProvider>

          <PatientTabSwitcher
            tabs={tabsWithContent}
            active={activeTab ?? ''}
            editMode={editMode}
            onChange={setActiveTab}
            onAddTab={addTab}
            onRemoveTab={removeTab}
            onSetVisible={setVisible}
            onMoveTo={moveTo}
            attention={
              role === 'psychiatrist' && masterPlan.value.reviewPending
                ? { [CARE_PLAN_TAB_ID]: 'needs review' }
                : undefined
            }
          />
        </Box>
      </CallNotesRequestProvider>
    </TabNavigationProvider>
  )
}

const AdvancedPatientView = ({ uid }: Props) => (
  <PatientRoleProvider uid={uid}>
    <PatientView key={uid} uid={uid} />
  </PatientRoleProvider>
)

export default AdvancedPatientView
